import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import RoomFormModal from '../roomFormModal';
import { useRooms } from '../../../hooks/useRooms';
import { useRoomLabel } from '../../../hooks/useRoomLabel';
import { useDeleteRoom } from '../hooks/useDeleteRoom';

function RoomListWeb() {
  const navigate = useNavigate();

  const roomLabel = useRoomLabel();
  const { rooms, isLoading, error: listError, refetch: getRooms } = useRooms();
  const { remove } = useDeleteRoom();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, roomId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRoomId(roomId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedRoom(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const room = rooms.find((r) => r.id === selectedRoomId);
    if (room) {
      setSelectedRoom(room);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRoom(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedRoomId(null);
  };

  const handleRoomSaved = () => {
    getRooms();
    setSuccessMessage(roomLabel.savedMessage);
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedRoomId)
      .then((res) => {
        getRooms();
        setSuccessMessage(res.data ?? roomLabel.deletedMessage);
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? roomLabel.deleteErrorMessage);
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedRoomId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                {roomLabel.plural}
              </Typography>
              <Button variant="outlined" onClick={openCreateModal} data-cy="add-room-btn">
                {roomLabel.addLabel}
              </Button>
            </Box>

            <List>
              {rooms.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  {roomLabel.emptyMessage}
                </Typography>
              ) : (
                rooms.map((room) => (
                  <React.Fragment key={room.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, room.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={room.name} />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={openEditModal}>Editar</MenuItem>
          <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
            Excluir
          </MenuItem>
        </Menu>

        <RoomFormModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={handleRoomSaved}
          room={selectedRoom}
        />

        <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>{roomLabel.deleteConfirm}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default RoomListWeb;
